<template>
  <div>
    <div
      class="flex select-none items-center space-x-1 leading-4"
      :class="{ 'opacity-50': disabled }"
    >
      <input
        :id="id || internalId"
        v-model="internalValue"
        :name="name"
        type="radio"
        :value="value"
        :disabled="disabled"
        class="size-4 rounded-full border text-transparent focus:outline-none enabled:cursor-pointer"
        :class="{
          white,
          'border-black-20 checked:border-primary': !white,
        }"
      />
      <label v-if="label || hasLabel" :for="id || internalId">
        <slot name="label">
          <span class="ui-text-xs" :class="{ 'text-white': white }"
            >{{ label }}
          </span>
        </slot>
      </label>
    </div>

    <FieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || hint"
      :class="{ 'text-white': white }"
    />
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import FieldHint from "~/components/shared/FieldHint.vue";

const props = withDefaults(
  defineProps<{
    modelValue: boolean | string | number | null | Array<any>;
    name: string;
    value: string;
    id?: string;
    label?: string;
    hideDetails?: boolean;
    disabled?: boolean;
    hint?: string;
    white?: boolean;
  }>(),
  {
    id: "",
    label: "",
    hint: "",
    white: false,
  },
);

const emit = defineEmits(["update:modelValue"]);

const slots = useSlots();

// const internalId = generateString();
const internalId = useId();
const name = toRef(props, "name");
const { errorMessage, handleChange } = useField(name, undefined, {
  initialValue: props.modelValue,
});

const hasLabel = computed(() => {
  return !!slots.label;
});

const internalValue = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    if (props.name) {
      // handleChange(props.modelValue);
      handleChange(newValue);
    }
    emit("update:modelValue", newValue);
  },
});
</script>

<style scoped>
.white {
  @apply checked:bg-white border-black-40 checked:border-white;
}
input.white[type="radio"]:checked {
  background-image: url("data:image/svg+xml;base64,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");
}
</style>
